/**
 * EhrHistoryView — the History tab of /tasks/ehr (docs/EHR_TASK_HISTORY_PLAN.md §8).
 *
 * One combined list of decided EHR tasks, residents and users together, newest first
 * (decision 10): approved, auto-approved and rejected. A row opens a read-only drawer with
 * what changed, or for a rejection, what the EHR proposed and why it was rejected.
 *
 * Loaded on demand from `getehrtaskhistory` whenever the range changes; it does not poll.
 * Nothing before 2026-10-06 19:00 UTC exists in History (decision 13), and the page says
 * so when the chosen range reaches back past it.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Drawer from '../../../../components/v2/Drawer.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import Pagination from '../../../../components/v2/Pagination.jsx';
import TableSkeleton from '../../../../components/v2/TableSkeleton.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import { exportEhrTaskHistoryCsv } from '../../../../services/csvExport.js';
import { fetchTaskHistory } from '../api/tasksEhrApi.js';
import { SortIcon } from './EhrTaskTable.jsx';

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const TASK_LABELS = {
  move_in: 'Move-in',
  update_info: 'Update Resident Info',
  relocate: 'Relocate',
  move_out: 'Move-out',
  user_add: 'Add User',
  user_remove: 'Remove User',
};

export const OUTCOME_LABELS = {
  approved: 'Approved',
  auto_approved: 'Auto-approved',
  rejected: 'Rejected',
};

const SUBJECT_TYPE_LABELS = { resident: 'Resident', user: 'User' };

/** History's first moment (decision 13): the release that began logging detailed changes. */
export const HISTORY_START_UTC = '2026-10-06 19:00:00';
const HISTORY_START_DATE = '2026-10-06';
const MAX_RANGE_DAYS = 366;
const DAY_MS = 86400000;

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom' },
];

const COLUMNS = [
  { key: 'decided_at', label: 'Decided', filterable: false },
  { key: 'task_label', label: 'Task', filterable: true },
  { key: 'subject_name', label: 'Subject', filterable: true, filterKey: 'subject_type_label', filterLabel: 'Subject' },
  { key: 'outcome_label', label: 'Outcome', filterable: true },
  { key: 'actor_name', label: 'Decided By', filterable: false },
];

const FILTER_LABELS = { task_label: 'Task', subject_type_label: 'Subject', outcome_label: 'Outcome' };

// ---------------------------------------------------------------------------
// Time helpers. The Lambda takes UTC 'YYYY-MM-DD HH:MM:SS'; the page works in campus-local
// days, using the campus tz_offset (signed hours, local = UTC + offset).
// ---------------------------------------------------------------------------

const pad = (n) => String(n).padStart(2, '0');

export function toUtcString(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} `
    + `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

/** Today's campus-local date as 'YYYY-MM-DD'. */
export function campusToday(offset, nowMs = Date.now()) {
  return toUtcString(nowMs + offset * 3600000).slice(0, 10);
}

/** A campus-local 'YYYY-MM-DD' at local midnight, in UTC ms; NaN when malformed. */
function localDateToUtcMs(dateStr, offset) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  if (!m) return NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - offset * 3600000;
}

/**
 * The UTC window for a range choice, or { error } when a custom range is unusable.
 * Custom dates are inclusive campus-local days: [start 00:00, end + 1 day 00:00).
 */
export function rangeWindow(range, customStart, customEnd, offset, nowMs = Date.now()) {
  if (range !== 'custom') {
    // A minute ahead, so an approval made a moment ago is inside the half-open window.
    const end = nowMs + 60000;
    return { startUtc: toUtcString(end - Number(range) * DAY_MS), endUtc: toUtcString(end) };
  }
  const start = localDateToUtcMs(customStart, offset);
  const end = localDateToUtcMs(customEnd, offset);
  if (Number.isNaN(start) || Number.isNaN(end)) return { error: 'Choose a start and end date.' };
  if (end < start) return { error: 'The end date is before the start date.' };
  if ((end - start) / DAY_MS + 1 > MAX_RANGE_DAYS) return { error: `Choose a range of ${MAX_RANGE_DAYS} days or less.` };
  return { startUtc: toUtcString(start), endUtc: toUtcString(end + DAY_MS) };
}

// ---------------------------------------------------------------------------
// Change / proposed formatting
// ---------------------------------------------------------------------------

const shown = (v) => (v === null || v === undefined || v === '' ? '(none)' : String(v));

/** `Field: before → after; Field: before → after` (CSV, decision 12). */
export function flattenChanges(changes) {
  return (changes || []).map((c) => `${c.l || c.f}: ${shown(c.o)} → ${shown(c.n)}`).join('; ');
}

/** `Field: value; Field: value` for what a rejected task proposed. */
export function flattenProposed(proposed) {
  return (proposed || []).map((p) => `${p.l || p.f}: ${shown(p.n)}`).join('; ');
}

const OUTCOME_CLASS = {
  approved: 'ehr-outcome--approved',
  auto_approved: 'ehr-outcome--auto',
  rejected: 'ehr-outcome--rejected',
};

const OutcomeBadge = ({ outcome }) => (
  <span className={`ehr-outcome ${OUTCOME_CLASS[outcome] || ''}`}>{OUTCOME_LABELS[outcome] || outcome}</span>
);

/** The subject's name, linked to its profile when the resident / user still exists. */
const SubjectLink = ({ row }) => {
  const name = row.subject_name || '—';
  if (!row.subject_exists || !row.entity_id) return <span className="dp-cell-text">{name}</span>;
  return (
    <Link
      to={`/profiles/${row.subject_type}/${row.entity_id}`}
      className="pp-field__value--link"
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EhrHistoryView = ({ campusId, tzOffset }) => {
  const offset = Number(tzOffset) || 0;

  const [range, setRange] = useState('30');
  // Custom opens on the last 30 days, but never before History's first day (decision 13).
  const [customStart, setCustomStart] = useState(() => {
    const monthAgo = toUtcString(Date.now() + offset * 3600000 - 29 * DAY_MS).slice(0, 10);
    return monthAgo < HISTORY_START_DATE ? HISTORY_START_DATE : monthAgo;
  });
  const [customEnd, setCustomEnd] = useState(() => campusToday(offset));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('decided_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();
  const [selected, setSelected] = useState(null);

  // A preset's window ends at the moment it was chosen; switching ranges re-takes "now".
  const window_ = useMemo(
    () => rangeWindow(range, customStart, customEnd, offset),
    [range, customStart, customEnd, offset],
  );

  // Only the latest request may land: a slow earlier range must not overwrite a newer one.
  const requestRef = useRef(0);
  useEffect(() => {
    if (window_.error) { setRows([]); setLoading(false); return; }
    const id = ++requestRef.current;
    setLoading(true);
    fetchTaskHistory(campusId, window_.startUtc, window_.endUtc)
      .then((data) => { if (id === requestRef.current) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (id === requestRef.current) setRows([]); })
      .finally(() => { if (id === requestRef.current) setLoading(false); });
  }, [campusId, window_]);

  /** Rows with their display labels, which the filters, sort and search work on. */
  const labelled = useMemo(() => rows.map((r) => ({
    ...r,
    task_label: TASK_LABELS[r.task_type] || r.task_type,
    outcome_label: OUTCOME_LABELS[r.outcome] || r.outcome,
    subject_type_label: SUBJECT_TYPE_LABELS[r.subject_type] || r.subject_type,
  })), [rows]);

  const filterValues = useCallback((key) => (
    [...new Set(labelled.map((r) => r[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b))
  ), [labelled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = labelled.filter((r) => Object.entries(filters).every(([k, vals]) => !vals.length || vals.includes(r[k])));
    if (q) {
      result = result.filter((r) => [r.subject_name, r.actor_name, r.reason]
        .some((v) => (v || '').toLowerCase().includes(q)));
    }
    const dir = sortOrder === 'asc' ? 1 : -1;
    return [...result].sort((a, b) => {
      const av = String(a[sortColumn] ?? '');
      const bv = String(b[sortColumn] ?? '');
      const cmp = sortColumn === 'decided_at' ? av.localeCompare(bv) : av.localeCompare(bv, undefined, { sensitivity: 'base', numeric: true });
      // Ties fall back to newest first, so equal names / outcomes keep a stable order.
      return cmp * dir || String(b.decided_at).localeCompare(String(a.decided_at));
    });
  }, [labelled, filters, search, sortColumn, sortOrder]);

  useEffect(() => { setPage(1); }, [filters, search, perPage, rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * perPage, safePage * perPage);

  const handleSort = useCallback((key) => {
    if (key === sortColumn) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortColumn(key); setSortOrder(key === 'decided_at' ? 'desc' : 'asc'); }
  }, [sortColumn]);

  const handleFilterChange = useCallback((key, values) => setFilters((f) => ({ ...f, [key]: values })), []);
  const handleRemoveFilter = useCallback((key, value) => setFilters((f) => ({
    ...f, [key]: (f[key] || []).filter((v) => v !== value),
  })), []);
  const handleClearFilters = useCallback(() => setFilters({}), []);

  const localTime = useCallback((utc) => (utc ? convertIsoUTCToLocal(utc, offset) : ''), [offset]);

  const handleExport = useCallback(() => {
    exportEhrTaskHistoryCsv(
      // Exactly what the filters and search show, newest first (plan §8).
      [...filtered].sort((a, b) => String(b.decided_at).localeCompare(String(a.decided_at))).map((r) => ({
        decided: localTime(r.decided_at),
        task: r.task_label,
        subject_type: r.subject_type_label,
        subject: r.subject_name,
        outcome: r.outcome_label,
        decided_by: r.actor_name,
        received: localTime(r.received_at),
        reason: r.reason,
        changes: flattenChanges(r.changes),
        proposed: flattenProposed(r.proposed),
      })),
      `ehr-task-history-${campusToday(offset)}.csv`,
    );
  }, [filtered, localTime, offset]);

  const reachesBeforeStart = !window_.error && window_.startUtc < HISTORY_START_UTC;

  return (
    <div className="dp-content ehr-history">
      <div className="dp-controls">
        <div className="dp-controls__left ehr-history-controls">
          <div className="dp-search">
            <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="search"
              placeholder="Search subject, decided by, reason..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="dp-search__input"
              aria-label="Search EHR task history"
            />
          </div>
          <select
            className="dp-select"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            aria-label="Date range"
          >
            {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {range === 'custom' ? (
            <div className="ehr-history-dates">
              <input
                type="date"
                className="dp-select ehr-history-date"
                value={customStart}
                min={HISTORY_START_DATE}
                max={customEnd || campusToday(offset)}
                onChange={(e) => setCustomStart(e.target.value)}
                aria-label="Start date"
              />
              <span className="ehr-history-dates__sep">to</span>
              <input
                type="date"
                className="dp-select ehr-history-date"
                value={customEnd}
                min={customStart || HISTORY_START_DATE}
                max={campusToday(offset)}
                onChange={(e) => setCustomEnd(e.target.value)}
                aria-label="End date"
              />
            </div>
          ) : null}
        </div>
        <div className="dp-controls__right">
          <button
            type="button"
            className="dp-btn dp-btn--secondary"
            onClick={handleExport}
            disabled={loading || filtered.length === 0}
            aria-label="Export task history to CSV"
          >
            <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {window_.error ? (
        <p className="ehr-history-note ehr-history-note--error" role="alert">{window_.error}</p>
      ) : reachesBeforeStart ? (
        <p className="ehr-history-note">Detailed available history begins Oct 6, 2026. For task decisions prior to this date reach out to PalCare support.</p>
      ) : null}

      <ActiveFilterBar
        filters={filters}
        columnLabels={FILTER_LABELS}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearFilters}
      />

      {loading ? (
        <TableSkeleton />
      ) : paginated.length === 0 ? (
        <div className="ehr-empty">
          <p className="ehr-empty__text">
            {rows.length === 0 ? 'No task decisions in this period' : 'No task decisions match these filters'}
          </p>
        </div>
      ) : (
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead className="dp-thead">
              <tr>
                <th scope="col" className="dp-th dp-th--actions ehr-td--review">
                  <span className="dp-sr-only">Details</span>
                </th>
                {COLUMNS.map((col) => {
                  const fKey = col.filterKey || col.key;
                  return (
                    <th key={col.key} scope="col" className="dp-th" aria-sort={ariaSort(sortColumn, sortOrder, col.key)}>
                      <div className="dp-th__content">
                        <button type="button" onClick={() => handleSort(col.key)} className="dp-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                          {col.label}
                          <SortIcon active={sortColumn === col.key} order={sortOrder} />
                        </button>
                        {col.filterable ? (
                          <FilterPopover
                            columnKey={fKey}
                            label={col.filterLabel || col.label}
                            values={filterValues(fKey)}
                            selectedValues={filters[fKey] || []}
                            onChange={handleFilterChange}
                          />
                        ) : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="dp-tbody">
              {paginated.map((r) => (
                /* The whole row still opens the drawer; the Details button is the visible,
                   keyboard-reachable way in, and gets focus back when the drawer closes. */
                <tr key={r.row_key} className="dp-tr ehr-task-row" onClick={() => setSelected(r)}>
                  <td className="dp-td dp-td--actions ehr-td--review">
                    <button
                      type="button"
                      className="dp-btn dp-btn--secondary dp-btn--sm"
                      aria-label={`View details for ${r.task_label}, ${r.subject_name || 'unknown'}`}
                      onClick={(e) => { e.stopPropagation(); setSelected(r); }}
                    >
                      Details
                      <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </td>
                  <td className="dp-td"><span className="dp-cell-text">{localTime(r.decided_at) || '—'}</span></td>
                  <td className="dp-td"><span className="dp-cell-text">{r.task_label}</span></td>
                  <td className="dp-td">
                    <SubjectLink row={r} />
                    <span className="ehr-subject-type">{r.subject_type_label}</span>
                  </td>
                  <td className="dp-td"><OutcomeBadge outcome={r.outcome} /></td>
                  <td className="dp-td"><span className="dp-cell-text">{r.actor_name || '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 ? (
        <Pagination
          page={safePage}
          totalPages={totalPages}
          total={filtered.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          entityLabel="decisions"
          selectId="dp-ehr-history-per-page"
        />
      ) : null}

      <HistoryDrawer row={selected} onClose={() => setSelected(null)} localTime={localTime} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Read-only detail drawer
// ---------------------------------------------------------------------------

const HistoryDrawer = ({ row, onClose, localTime }) => {
  if (!row) return null;
  const isRejected = row.outcome === 'rejected';
  const changes = row.changes || [];
  const proposed = row.proposed || [];

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${row.task_label}: ${row.subject_name || '—'}`}
      footer={<button type="button" className="dp-btn dp-btn--secondary" onClick={onClose}>Close</button>}
    >
      {/* One block child: the drawer body is a flex column, where .dp-table-wrap's flex: 1
          would stretch the first table to the bottom of the panel. */}
      <div className="ehr-history-drawer">
      <div className="ehr-history-drawer__status">
        <OutcomeBadge outcome={row.outcome} />
        <span className="ehr-subject-type">{row.subject_type_label}</span>
      </div>

      <div className="pp-field-grid ehr-history-drawer__facts">
        <div className="pp-field">
          <span className="pp-field__label">Subject</span>
          <span className="pp-field__value"><SubjectLink row={row} /></span>
        </div>
        <div className="pp-field">
          <span className="pp-field__label">Decided By</span>
          <span className="pp-field__value">{row.actor_name || '—'}</span>
        </div>
        <div className="pp-field">
          <span className="pp-field__label">Decided</span>
          <span className="pp-field__value">{localTime(row.decided_at) || '—'}</span>
        </div>
        <div className="pp-field">
          <span className="pp-field__label">Received</span>
          <span className={row.received_at ? 'pp-field__value' : 'pp-field__value pp-field__value--muted'}>
            {localTime(row.received_at) || '—'}
          </span>
        </div>
      </div>

      {isRejected ? (
        <>
          <h3 className="ehr-history-drawer__heading">Reason</h3>
          <p className="ehr-history-drawer__reason">{row.reason || '—'}</p>

          <h3 className="ehr-history-drawer__heading">Proposed</h3>
          {proposed.length ? (
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr><th scope="col" className="dp-th">Field</th><th scope="col" className="dp-th">Proposed</th></tr>
                </thead>
                <tbody className="dp-tbody">
                  {proposed.map((p) => (
                    <tr key={p.f} className="dp-tr">
                      <td className="dp-td"><span className="dp-cell-text">{p.l || p.f}</span></td>
                      <td className="dp-td"><span className="dp-cell-text">{shown(p.n)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ehr-history-note">No proposed values were recorded.</p>
          )}
        </>
      ) : (
        <>
          <h3 className="ehr-history-drawer__heading">Changes</h3>
          {changes.length ? (
            <div className="dp-table-wrap">
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th scope="col" className="dp-th">Field</th>
                    <th scope="col" className="dp-th">Before</th>
                    <th scope="col" className="dp-th">After</th>
                  </tr>
                </thead>
                <tbody className="dp-tbody">
                  {changes.map((c, i) => (
                    <tr key={`${c.f}-${i}`} className="dp-tr">
                      <td className="dp-td"><span className="dp-cell-text">{c.l || c.f}</span></td>
                      <td className="dp-td"><span className="dp-cell-text">{shown(c.o)}</span></td>
                      <td className="dp-td"><span className="dp-cell-text">{shown(c.n)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ehr-history-note">Changes weren&rsquo;t recorded for this decision.</p>
          )}
          {row.trunc ? (
            <p className="ehr-history-note">Some values were too long to record in full.</p>
          ) : null}
        </>
      )}
      </div>
    </Drawer>
  );
};

export default EhrHistoryView;
